<template>
  <div
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    style="background: rgba(0, 0, 0, 0.5)"
    @click.self="onClose"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("Email delivery trackings") }}</h5>
          <button
            type="button"
            class="btn-close"
            :aria-label="$t('Close')"
            @click="onClose"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="isLoading" class="text-center p-3">
            <LoadingSpinner>
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </LoadingSpinner>
          </div>
          <div v-else-if="emailDeliveryTrackings?.items.length" class="table-responsive">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>{{ $t("Recipient email") }}</th>
                  <th>{{ $t("Status") }}</th>
                  <th>{{ $t("Sent at") }}</th>
                  <th>{{ $t("Delivered at") }}</th>
                  <th>{{ $t("Error details") }}</th>
                  <th>{{ $t("Failed at") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="emailDeliveryTracking in emailDeliveryTrackings?.items"
                  :key="emailDeliveryTracking.id"
                >
                  <td>{{ emailDeliveryTracking.recipientEmail }}</td>
                  <td>{{ $t(`emailDeliveryTrackings.status.${emailDeliveryTracking.status}`) }}</td>
                  <td>
                    {{
                      emailDeliveryTracking.sentAt
                        ? moment(emailDeliveryTracking.sentAt).format(dateTimeFormat)
                        : "&mdash;"
                    }}
                  </td>
                  <td>
                    {{
                      emailDeliveryTracking.deliveredAt
                        ? moment(emailDeliveryTracking.deliveredAt).format(dateTimeFormat)
                        : "&mdash;"
                    }}
                  </td>
                  <td>{{ emailDeliveryTracking.errorDetails ?? "&mdash;" }}</td>
                  <td>
                    {{
                      emailDeliveryTracking.failedAt
                        ? moment(emailDeliveryTracking.failedAt).format(dateTimeFormat)
                        : "&mdash;"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center p-3 text-muted">
            {{ $t("No email delivery trackings found") }}
          </div>

          <div v-if="showPagination" class="mt-4 d-flex justify-content-center">
            <b-pagination
              v-model="currentPage"
              :total-rows="emailDeliveryTrackings?.totalResults ?? 0"
              :per-page="emailDeliveryTrackings?.perPage ?? 10"
              align="center"
              @update:model-value="onPageChange"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="onClose">
            {{ $t("Close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";
import { useGetEmailDeliveryTrackingsQuery } from "../queries/useGetEmailDeliveryTrackingsQuery";

const newUiEnabled = useNewUiEnabled();
const dateTimeFormat = computed(() =>
  newUiEnabled.value ? "DD.MM.YYYY HH:mm:ss" : "YYYY-MM-DD HH:mm",
);

const router = useRouter();

const props = defineProps({
  automatedReportId: {
    type: Number,
    required: true,
  },
});

const currentPage = ref(1);

const { data: emailDeliveryTrackings, isLoading } = useGetEmailDeliveryTrackingsQuery(
  props.automatedReportId,
  currentPage,
);

const showPagination = computed(() => {
  return (
    (emailDeliveryTrackings.value?.totalResults ?? 0) >
    (emailDeliveryTrackings.value?.perPage ?? 10)
  );
});

const onClose = () => router.push({ name: "automatedReports.list" });

const onPageChange = (page: number) => (currentPage.value = page);
</script>
